import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { seatNumberFromSipUser, type LiveExtension, type LiveQueue, type LiveQueueAgent } from '@arcnet/contracts';
import { useLiveScope, useLiveState } from './useLiveState.js';
import { agentState, stateTime } from './agentstate.js';
import { api } from '../../lib/api/client.js';
import { useTenants } from '../../lib/api/hooks.js';
import { Badge, Empty, Stat, StatGroup } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { formatDuration, formatNumber, relativeTime } from '../../lib/format.js';
import { isUnreachable } from '../extensions/ExtensionPresence.js';

/**
 * The account's queues and the agents in them, as they stand this second (rebuilt 30 September
 * 2026, owner: "need to be able to see the extensions in a row by row, also the calls they have
 * taken, the current status etc clean it up, also have a queue filter").
 *
 * Two tables. Queues: who is waiting, and today's answered and abandoned. Agents: one row per
 * extension across the chosen queues, with where they stand now and what today has given them.
 *
 * The live half arrives on the queues and calls channels: mod_callcenter's own events tell the node
 * when to re-read a queue, and the node pushes the moment it differs. Waits and timers count up in
 * the browser from when they started, so somebody merely waiting sends nothing. Today's figures are
 * the queue history from the account's midnight (/wallboard, /wallboard/agents), read again
 * whenever the live picture moves and every 15 seconds.
 */

/** Seconds the longest-waiting caller has waited, at `now` on the switch's clock. */
function waitSec(q: LiveQueue, now: number): number {
  if (q.longest_waiting_since == null) return q.waiting > 0 ? q.longest_wait_sec : 0;
  return Math.max(0, Math.floor((now - q.longest_waiting_since) / 1000));
}

/** Thresholds rather than a gradient: a supervisor is scanning for the one row that needs them. */
function waitTone(sec: number): 'ok' | 'warn' | 'danger' {
  if (sec >= 120) return 'danger';
  if (sec >= 30) return 'warn';
  return 'ok';
}

interface Seat { number: string; sip_domain: string; display_name: string; registered: boolean; ping_status?: string | null }
interface QueueToday { queue_key: string | null; answered: number; abandoned: number; service_level_pct: number | null }
interface AgentToday { queue_key: string; number: string; calls_taken: number; missed: number; talk_sec: number; last_call_at: string | null }
interface LastCall { queue_key: string; number: string; at: string }

/** The chosen queues, remembered per account in this browser. Empty means every queue. */
function useQueueChoice(account: string | null): [string[], (next: string[]) => void] {
  const key = `arc.liveQueues.${account ?? 'all'}`;
  const read = (): string[] => {
    try {
      const v = JSON.parse(localStorage.getItem(key) ?? '[]') as unknown;
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    } catch { return []; }
  };
  const [chosen, setChosen] = useState<string[]>(read);
  // Another account's choice when the account changes.
  useEffect(() => { setChosen(read()); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (next: string[]) => {
    setChosen(next);
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* a private window keeps it for the visit */ }
  };
  return [chosen, set];
}

export function LiveQueuesPage() {
  const nav = useNavigate();
  // Calls too, so an agent busy on a call that did not come from a queue says so.
  const { queues, extensions, connection, streamed, nodes, skewMs } = useLiveState(['queues', 'calls']);
  // The account open in the console, or the signed-in scope outside one.
  const { platform, tenantId: activeTenantId } = useLiveScope();
  const [chosen, setChosen] = useQueueChoice(activeTenantId);
  const [onlyBusy, setOnlyBusy] = useState(false);

  // ---- the clock: one render a second, only while something on screen is counting
  const ticking = queues.some((q) => q.longest_waiting_since != null
    || (q.agents ?? []).some((a) => a.ready_at !== null
      || (a.since !== null && (a.status === 'On Break' || a.state === 'In a queue call'))))
    || extensions.some((e) => e.state === 'on_call');
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    if (!ticking) return;
    setClock(Date.now());
    const t = setInterval(() => setClock(Date.now()), 1_000);
    return () => clearInterval(t);
  }, [ticking]);
  const now = clock + skewMs;

  // ---- names and phones (shared with Live extensions), and today's figures
  const seatsQ = useQuery({
    queryKey: ['live-extensions', activeTenantId],
    queryFn: () => api.get<{ data: Seat[] }>(`/v1/live/extensions${activeTenantId ? `?tenant_id=${activeTenantId}` : ''}`),
    enabled: platform || !!activeTenantId,
    refetchInterval: 60_000,
  });
  const seats = useMemo(() => new Map((seatsQ.data?.data ?? []).map((e) => [`${e.number}@${e.sip_domain}`, e])), [seatsQ.data]);
  // By seat: a channel is named by its SIP login (1001-meridian), an agent by its number.
  const calls = useMemo(() => new Map<string, LiveExtension>(
    extensions.map((e) => [`${seatNumberFromSipUser(e.user, e.domain) ?? e.user}@${e.domain}`, e])), [extensions]);

  // Today's figures belong to one account; across the platform they are not shown.
  const dayShown = !platform && !!activeTenantId;
  const todayQ = useQuery({
    queryKey: ['live-queues-today', activeTenantId],
    queryFn: () => api.get<{ queues: QueueToday[] }>(`/v1/tenants/${activeTenantId}/wallboard`),
    enabled: dayShown, refetchInterval: 15_000,
  });
  const agentsTodayQ = useQuery({
    queryKey: ['live-agents-today', activeTenantId],
    queryFn: () => api.get<{ agents: AgentToday[]; last_calls?: LastCall[] }>(`/v1/tenants/${activeTenantId}/wallboard/agents`),
    enabled: dayShown, refetchInterval: 15_000,
  });
  // A call answered or ended moves the live picture: read the day again straight after.
  const movement = queues.map((q) => `${q.queue}:${q.waiting}:${q.agents_on_call}`).join('|');
  const { refetch: refetchToday } = todayQ;
  const { refetch: refetchAgentsToday } = agentsTodayQ;
  useEffect(() => {
    if (!dayShown) return;
    const t = setTimeout(() => { void refetchToday(); void refetchAgentsToday(); }, 1_500);
    return () => clearTimeout(t);
  }, [movement, dayShown, refetchToday, refetchAgentsToday]);
  const today = useMemo(() => new Map((todayQ.data?.queues ?? []).map((q) => [q.queue_key ?? '', q])), [todayQ.data]);

  // ---- which queues
  const all = useMemo(() => [...queues].sort((a, b) => a.name.localeCompare(b.name)), [queues]);
  const known = new Set(all.map((q) => q.queue));
  const picked = chosen.filter((k) => known.has(k));
  const shown = all.filter((q) => (picked.length === 0 || picked.includes(q.queue)) && (!onlyBusy || q.waiting > 0));
  const toggle = (key: string) => setChosen(picked.includes(key) ? picked.filter((k) => k !== key) : [...picked, key]);

  /** SIP domain to tenant id, for the drill-through into the queue's history. */
  const tenants = useTenants({ limit: 200 });
  const byDomain = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tenants.data?.data ?? []) m.set(t.sip_domain, t.id);
    if (!platform && activeTenantId) for (const q of queues) m.set(q.domain, activeTenantId);
    return m;
  }, [tenants.data, queues, platform, activeTenantId]);

  /** A phone that cannot ring: not registered, or not answering the switch's pings. */
  const phoneDown = (k: string) => {
    const s = seats.get(k);
    return s !== undefined && (!s.registered || isUnreachable(s.ping_status));
  };
  /** Ready by the same rule the agents table uses, not the switch's count (which ignores phones). */
  const readyIn = (q: LiveQueue) => (q.agents
    ? q.agents.filter((a) => agentState(a, now, calls.get(`${a.agent}@${q.domain}`), phoneDown(`${a.agent}@${q.domain}`)).free).length
    : q.agents_available);

  // ---- one row per agent across the shown queues
  const agents = useMemo(() => {
    const dayOf = new Map<string, AgentToday[]>();
    for (const r of agentsTodayQ.data?.agents ?? []) {
      const k = `${r.number}@${r.queue_key.split('@')[1] ?? ''}`;
      dayOf.set(k, [...(dayOf.get(k) ?? []), r]);
    }
    const rows = new Map<string, { number: string; live: LiveQueueAgent; queues: string[]; keys: string[] }>();
    for (const q of shown) {
      for (const a of q.agents ?? []) {
        const k = `${a.agent}@${q.domain}`;
        const row = rows.get(k);
        if (row) { row.queues.push(q.name); row.keys.push(q.queue); }
        else rows.set(k, { number: a.agent, live: a, queues: [q.name], keys: [q.queue] });
      }
    }
    // The last queue call each agent took, on any day (the day's figures above start at midnight).
    const lastOf = new Map<string, LastCall[]>();
    for (const r of agentsTodayQ.data?.last_calls ?? []) {
      const k = `${r.number}@${r.queue_key.split('@')[1] ?? ''}`;
      lastOf.set(k, [...(lastOf.get(k) ?? []), r]);
    }
    return [...rows.entries()].map(([k, r]) => {
      const s = seats.get(k);
      const state = agentState(r.live, now, calls.get(k), s !== undefined && (!s.registered || isUnreachable(s.ping_status)));
      const day = (dayOf.get(k) ?? []).filter((d) => r.keys.includes(d.queue_key));
      const last = [...day.map((d) => d.last_call_at), ...(lastOf.get(k) ?? []).filter((d) => r.keys.includes(d.queue_key)).map((d) => d.at)]
        .filter((x): x is string => !!x).sort().at(-1) ?? null;
      return {
        key: k, number: r.number, seat: seats.get(k), state, queues: r.queues,
        taken: day.reduce((n, d) => n + d.calls_taken, 0),
        missed: day.reduce((n, d) => n + d.missed, 0),
        talk: day.reduce((n, d) => n + d.talk_sec, 0),
        last,
      };
    }).sort((a, b) => a.state.rank - b.state.rank || a.number.localeCompare(b.number, undefined, { numeric: true }));
  }, [shown, agentsTodayQ.data, calls, seats, now]);

  const totals = {
    waiting: shown.reduce((n, q) => n + q.waiting, 0),
    ready: shown.reduce((n, q) => n + readyIn(q), 0),
    answered: shown.reduce((n, q) => n + (today.get(q.queue)?.answered ?? 0), 0),
    abandoned: shown.reduce((n, q) => n + (today.get(q.queue)?.abandoned ?? 0), 0),
  };
  const longest = shown.reduce((n, q) => Math.max(n, waitSec(q, now)), 0);
  const unstaffed = shown.filter((q) => q.waiting > 0 && readyIn(q) === 0);
  const dash = <span className="arc-subtle">—</span>;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Live queues</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>Callers waiting and where every agent stands, right now</p>
        </div>
        <Badge tone={connection === 'live' ? 'ok' : 'warn'}>
          <span className={connection === 'live' ? 'arc-dot' : 'arc-dot arc-pulse'} aria-hidden="true" />
          {connection === 'live' ? (streamed ? 'live' : 'waiting for data') : connection}
        </Badge>
      </div>

      {/* Which queues: none picked is every queue. */}
      {all.length > 0 && (
        <div className="arc-lq-filter" role="group" aria-label="Queues shown">
          <span className="arc-muted" style={{ fontSize: 12.5 }}>Queues</span>
          <button type="button" className={`arc-lq-chip${picked.length === 0 ? ' is-on' : ''}`}
            aria-pressed={picked.length === 0} onClick={() => setChosen([])}>All</button>
          {all.map((q) => (
            <button key={q.queue} type="button" className={`arc-lq-chip${picked.includes(q.queue) ? ' is-on' : ''}`}
              aria-pressed={picked.includes(q.queue)} onClick={() => toggle(q.queue)}>
              {q.name}
              {q.waiting > 0 && <span className="arc-lq-chip-n">{q.waiting}</span>}
            </button>
          ))}
          <span className="arc-spacer" />
          <label className="arc-row" style={{ gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyBusy} onChange={(e) => setOnlyBusy(e.target.checked)} />
            Only queues with callers waiting
          </label>
        </div>
      )}

      <StatGroup>
        <Stat bare label="Waiting" value={formatNumber(totals.waiting)} icon="clock" hue={2}
          tone={totals.waiting > 0 ? 'warn' : undefined}
          sub={longest > 0 ? `longest ${formatDuration(longest)}` : 'nobody in a queue'} />
        <Stat bare label="Agents ready" value={formatNumber(totals.ready)} icon="users" hue={4}
          tone={totals.waiting > 0 && totals.ready === 0 ? 'danger' : undefined}
          sub={`of ${formatNumber(agents.filter((a) => a.state.rank < 8).length)} logged on`} />
        <Stat bare label="Answered today" value={dayShown ? formatNumber(totals.answered) : '—'} icon="phone" hue={1}
          sub={dayShown ? 'since midnight' : 'shown per account'} />
        <Stat bare label="Abandoned today" value={dayShown ? formatNumber(totals.abandoned) : '—'} icon="warning" hue={3}
          tone={totals.abandoned > 0 ? 'warn' : undefined}
          sub={dayShown ? 'hung up before an answer' : 'shown per account'} />
      </StatGroup>

      {nodes === 0 && (
        <div className="arc-banner arc-banner-warn" style={{ margin: '14px 0 0' }} role="alert">
          <Icon name="warning" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>No node is reporting</div>
            <div style={{ opacity: .85, marginTop: 2 }}>These figures are the last ones received and are not current.</div>
          </div>
        </div>
      )}
      {unstaffed.length > 0 && (
        <div className="arc-banner arc-banner-danger" style={{ margin: '14px 0 0' }} role="alert">
          <Icon name="warning" size={14} />
          <div style={{ fontWeight: 600 }}>
            {unstaffed.length === 1
              ? `${unstaffed[0]!.name} has callers waiting and no agent ready`
              : `${unstaffed.length} queues have callers waiting and no agent ready`}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------- queues */}
      <div className="arc-card" style={{ marginTop: 14 }}>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Queue</th>
                <th className="arc-td-num">Waiting</th>
                <th className="arc-td-num">Longest wait</th>
                <th className="arc-td-num">Ready</th>
                <th className="arc-td-num">On a call</th>
                <th className="arc-td-num">Logged on</th>
                <th className="arc-td-num">Answered</th>
                <th className="arc-td-num">Abandoned</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((q) => {
                const w = waitSec(q, now);
                const t = today.get(q.queue);
                const on = (q.agents ?? []).filter((a) => a.status !== 'Logged Out').length;
                return (
                  <tr key={q.queue}>
                    <td>
                      <span style={{ fontWeight: 550 }}>{q.name}</span>
                      {platform && <span className="arc-subtle" style={{ fontSize: 11, marginLeft: 8 }}>{q.domain}</span>}
                    </td>
                    <td className="arc-td-num">
                      {q.waiting > 0 ? <Badge tone={readyIn(q) === 0 ? 'danger' : 'warn'}>{formatNumber(q.waiting)}</Badge> : dash}
                    </td>
                    <td className="arc-td-num">
                      {q.waiting > 0
                        ? <span className="arc-num" style={{ color: `var(--arc-${waitTone(w)})`, fontWeight: 600 }}>{formatDuration(w)}</span>
                        : dash}
                    </td>
                    <td className="arc-td-num" style={{ fontWeight: 600, color: readyIn(q) === 0 && q.waiting > 0 ? 'var(--arc-danger)' : undefined }}>
                      {formatNumber(readyIn(q))}
                    </td>
                    <td className="arc-td-num">{formatNumber(q.agents_on_call)}</td>
                    <td className="arc-td-num arc-muted">{q.agents ? `${on} of ${q.agents.length}` : formatNumber(q.agents_total)}</td>
                    <td className="arc-td-num">{dayShown && t ? formatNumber(t.answered) : dash}</td>
                    <td className="arc-td-num">
                      {dayShown && t ? (t.abandoned > 0 ? <span style={{ color: 'var(--arc-warn)', fontWeight: 600 }}>{t.abandoned}</span> : '0') : dash}
                    </td>
                    <td className="arc-td-right">
                      {byDomain.get(q.domain) && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" title={`Queue performance for ${q.name}`}
                          onClick={() => void nav({ to: '/manage/$tenantId/$', params: { tenantId: byDomain.get(q.domain)!, _splat: 'queue-reports' } })}>
                          History
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {queues.length === 0 && (
          <Empty icon="queue" title={streamed ? 'No queues' : 'Waiting for the switch'}
            hint={streamed ? 'This account has no queues. Create one under Call routing › Queues.' : 'Connecting to the live event stream.'} />
        )}
        {queues.length > 0 && shown.length === 0 && (
          <Empty icon="check" title="Nobody is waiting" hint="No chosen queue has callers waiting." />
        )}
      </div>

      {/* ---------------------------------------------------------------- agents */}
      {shown.length > 0 && (
        <div className="arc-card" style={{ marginTop: 14 }}>
          <div className="arc-card-head">
            <div>
              <h2 className="arc-h2">Agents</h2>
              <p className="arc-muted" style={{ fontSize: 12.5, margin: '2px 0 0' }}>
                Everybody in the queues shown, busiest first.
                {dayShown ? ' Calls count from midnight, in these queues only.' : ''}
              </p>
            </div>
          </div>
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Status</th>
                  <th>Phone</th>
                  {shown.length > 1 && <th>Queues</th>}
                  <th className="arc-td-num">Taken</th>
                  <th className="arc-td-num" title="Callers offered to this agent who did not reach them">Missed</th>
                  <th className="arc-td-num">Talk time</th>
                  <th className="arc-td-right">Last call</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => {
                  const down = a.seat !== undefined && (!a.seat.registered || isUnreachable(a.seat.ping_status));
                  return (
                    <tr key={a.key}>
                      <td>
                        <span className="arc-mono" style={{ marginRight: 10 }}>{a.number}</span>
                        <span style={{ fontWeight: 550 }}>{a.seat?.display_name ?? <span className="arc-subtle">unknown extension</span>}</span>
                      </td>
                      <td>
                        <span className="arc-row" style={{ gap: 7 }}>
                          <span className={`arc-lq-dot is-${a.state.tone}`} aria-hidden="true" />
                          <span style={{ fontWeight: 550, color: a.state.tone === 'muted' ? 'var(--arc-fg-3)' : `var(--arc-${a.state.tone})` }}>
                            {a.state.label}
                          </span>
                          {a.state.secs !== null && (
                            <span className="arc-subtle arc-num">
                              {a.state.label === 'Wrap-up' ? `${a.state.secs}s left` : stateTime(a.state.secs, formatDuration)}
                            </span>
                          )}
                        </span>
                      </td>
                      <td>
                        {a.seat === undefined ? dash
                          : down
                            ? <Badge tone={a.state.rank === 6 ? 'danger' : a.state.rank === 8 ? 'muted' : 'warn'}>offline</Badge>
                            : <span className="arc-muted">online</span>}
                      </td>
                      {shown.length > 1 && <td className="arc-muted">{a.queues.join(', ')}</td>}
                      <td className="arc-td-num" style={{ fontWeight: 600 }}>{dayShown ? formatNumber(a.taken) : dash}</td>
                      <td className="arc-td-num">{dayShown ? (a.missed > 0 ? <span style={{ color: 'var(--arc-warn)' }}>{a.missed}</span> : '0') : dash}</td>
                      <td className="arc-td-num arc-num">{dayShown ? formatDuration(a.talk) : dash}</td>
                      <td className="arc-td-right arc-muted">{a.last ? relativeTime(a.last) : dash}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {agents.length === 0 && (
            <Empty icon="users" title="No agents" hint="Add agents to a queue under Call routing › Queues." />
          )}
        </div>
      )}
    </>
  );
}
